import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, ClipboardList, Eye, Edit2, ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { orderService, clientService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_production', label: 'In Production' },
  { value: 'partial', label: 'Partial' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    client_id: '',
    customer_order_ref: '',
    order_date: new Date().toISOString().split('T')[0],
    required_delivery_date: '',
    priority: 'normal',
    notes: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [search, filterClient, filterStatus]);

  const loadClients = async () => {
    try {
      const response = await clientService.getAll({ is_active: true });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const loadOrders = async () => {
    try {
      const params = { search };
      if (filterClient) params.client_id = filterClient;
      if (filterStatus) params.status = filterStatus;
      const response = await orderService.getAll(params);
      setOrders(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento ordini');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.required_delivery_date) delete data.required_delivery_date;
      
      if (editingOrder) {
        await orderService.update(editingOrder.id, data);
        toast.success('Ordine aggiornato');
      } else {
        await orderService.create(data);
        toast.success('Ordine creato');
      }
      setDialogOpen(false);
      resetForm();
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operazione fallita');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Eliminare l'ordine ${order.order_number}? Verranno eliminate anche tutte le righe associate.`)) return;
    try {
      await orderService.delete(order.id);
      toast.success('Ordine eliminato');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };


  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      client_id: order.client_id,
      customer_order_ref: order.customer_order_ref || '',
      order_date: order.order_date,
      required_delivery_date: order.required_delivery_date || '',
      priority: order.priority,
      notes: order.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingOrder(null);
    setFormData({
      client_id: '',
      customer_order_ref: '',
      order_date: new Date().toISOString().split('T')[0],
      required_delivery_date: '',
      priority: 'normal',
      notes: ''
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-sky-500 text-white',
      in_production: 'bg-amber-500 text-white',
      partial: 'bg-violet-500 text-white',
      closed: 'bg-slate-400 text-white',
    };
    return styles[status] || styles.open;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      normal: 'bg-slate-200 text-slate-700',
      high: 'bg-orange-500 text-white',
      urgent: 'bg-red-600 text-white',
    };
    return styles[priority] || styles.normal;
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ordini</h1>
          <p className="text-sm text-slate-500 mt-1">Gestisci ordini e commesse</p>
        </div>
        {hasRole('admin', 'quality', 'production') && (
          <Button onClick={openNewDialog} className="h-12 px-6 text-base" data-testid="add-order-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Ordine
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per numero ordine, rif. cliente o Lotto Cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-orders-input"
              />
            </div>
            <Select value={filterClient || "all"} onValueChange={(v) => setFilterClient(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>N. Ordine</th>
                    <th>Cliente</th>
                    <th>Rif. Cliente</th>
                    <th>Data Ordine</th>
                    <th>Data Consegna</th>
                    <th>Priorità</th>
                    <th>Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="row-link" onClick={() => navigate(`/orders/${order.id}`)} data-testid={`order-row-${order.id}`}>
                      <td className="font-mono font-medium">{order.order_number}</td>
                      <td className="font-medium">{order.client_name || '-'}</td>
                      <td className="text-slate-600">{order.customer_order_ref || '-'}</td>
                      <td>{order.order_date}</td>
                      <td>{order.required_delivery_date || '-'}</td>
                      <td>
                        <Badge className={getPriorityBadge(order.priority)}>
                          {order.priority}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={getStatusBadge(order.status)}>
                          {order.status?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {hasRole('admin', 'quality', 'production') && (
                            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleEdit(order)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {hasRole('admin') && (
                            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleDeleteOrder(order)} data-testid={`delete-order-${order.id}`}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingOrder ? 'Modifica Ordine' : 'Nuovo Ordine'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  required
                >
                  <SelectTrigger data-testid="order-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Customer Order Reference</Label>
                <Input
                  value={formData.customer_order_ref}
                  onChange={(e) => setFormData({ ...formData, customer_order_ref: e.target.value })}
                  placeholder="PO-12345"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Order Date *</Label>
                  <Input
                    type="date"
                    value={formData.order_date}
                    onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Required Delivery</Label>
                  <Input
                    type="date"
                    value={formData.required_delivery_date}
                    onChange={(e) => setFormData({ ...formData, required_delivery_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="h-11 px-5" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="h-11 px-6" data-testid="save-order-btn">
                {editingOrder ? 'Aggiorna' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
